let tooltipIdCounter = 0;
const ATTRIBUTES = {
  DATA_VISIBLE: 'data-visible',
  ARIA_HIDDEN: 'aria-hidden',
  ARIA_DESCRIBEDBY: 'aria-describedby',
};

class CustomTooltip extends HTMLElement {
  constructor() {
    super();

    this.trigger = null;
    this.tooltip = null;
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    const text = (this.getAttribute('text') || '').trim();
    const trigger = this.firstElementChild;

    if (!text || !(trigger instanceof HTMLElement)) return;

    this.trigger = trigger;

    // Ensure focusable for accessibility
    if (!this.trigger.hasAttribute('tabindex')) {
      this.trigger.tabIndex = 0;
    }

    this.tooltip = this.createTooltip(this.trigger, text);
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
    this.tooltip?.remove();
    this.tooltip = null;
    this.trigger = null;
  }

  bindEvents() {
    if (!this.trigger) return;

    this.trigger.addEventListener('pointerenter', this.show);
    this.trigger.addEventListener('pointerleave', this.hide);
    this.trigger.addEventListener('focus', this.show);
    this.trigger.addEventListener('blur', this.hide);
    this.trigger.addEventListener('keydown', this.onKeydown);
  }

  unbindEvents() {
    if (!this.trigger) return;

    this.trigger.removeEventListener('pointerenter', this.show);
    this.trigger.removeEventListener('pointerleave', this.hide);
    this.trigger.removeEventListener('focus', this.show);
    this.trigger.removeEventListener('blur', this.hide);
    this.trigger.removeEventListener('keydown', this.onKeydown);
  }

  show() {
    if (!this.trigger || !this.tooltip) return;

    const rect = this.trigger.getBoundingClientRect();
    const tooltip = this.tooltip;

    tooltip.setAttribute(ATTRIBUTES.DATA_VISIBLE, 'true');
    tooltip.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'false');
    tooltip.style.position = 'fixed';
    tooltip.style.visibility = 'hidden';

    const width = tooltip.offsetWidth;
    const height = tooltip.offsetHeight;

    // Default position: horizontally centered below the trigger
    // - Start at the trigger's left edge
    // - Move right to the trigger's horizontal midpoint
    // - Then shift left by half the tooltip's width so they line up in the middle
    let left = rect.left + rect.width / 2 - width / 2;
    let top = rect.bottom + 8;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // If the tooltip would go off the LEFT side of the screen, pin it 4px from the left edge
    if (left < 4) left = 4;

    // If the tooltip would go off the RIGHT side of the screen, pin it 4px from the right edge
    if (left + width > vw - 4) left = vw - width - 4;

    // If the tooltip would go off the BOTTOM of the screen...
    if (top + height > vh - 4) {
      // ...flip it to appear ABOVE the trigger instead (again with an 8px gap)
      top = rect.top - height - 8;

      // If there's not enough room above either, just pin it 4px from the top
      // rather than letting it disappear off-screen
      if (top < 4) top = 4;
    }

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
    tooltip.style.visibility = '';
  }

  hide() {
    if (!this.tooltip) return;

    this.tooltip.setAttribute(ATTRIBUTES.DATA_VISIBLE, 'false');
    this.tooltip.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
  }

  /**
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    if (event.key === 'Escape') {
      this.hide();
      this.trigger?.blur();
    }
  }

  /**
   * @param {HTMLElement} trigger
   * @param {string} text
   */
  createTooltip(trigger, text) {
    const id = `tooltip-${++tooltipIdCounter}`;
    const tone = this.getAttribute('tone');

    const el = document.createElement('div');
    el.className = `tooltip tooltip--${tone}`;
    el.id = id;
    el.setAttribute('role', 'tooltip');
    el.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
    el.textContent = text;

    document.body.appendChild(el);
    trigger.setAttribute(ATTRIBUTES.ARIA_DESCRIBEDBY, id);

    return el;
  }
}

if (!customElements.get('custom-tooltip')) {
  customElements.define('custom-tooltip', CustomTooltip);
}
